"use client";

import * as React from "react";
import { ApiProvider, useSession } from "@repo/api-client";
import {
  EntitlementProvider,
  PermissionProvider,
  ToastProvider,
} from "@repo/ui";
import { LocaleProvider } from "@/locales";
import { initPortalSentry } from "../lib/sentry";
import { isDevBuild, portalApiUrl, portalUseMsw } from "../lib/public-env";

function Gates({ children }: { children: React.ReactNode }) {
  const { user } = useSession();
  return (
    <PermissionProvider permissions={user?.permissions ?? []}>
      <EntitlementProvider entitlements={user?.entitlements ?? {}}>
        {children}
      </EntitlementProvider>
    </PermissionProvider>
  );
}

export function Providers({ children }: { children: React.ReactNode }) {
  const baseUrl = portalApiUrl();

  React.useEffect(() => {
    initPortalSentry("client-portal");
  }, []);

  React.useEffect(() => {
    if (!portalUseMsw()) return;
    if (!isDevBuild()) return;
    let cancelled = false;
    void (async () => {
      const { setupWorker } = await import("msw/browser");
      const { http, HttpResponse } = await import("msw");
      const { createHandlers } = await import("@repo/mocks");
      if (cancelled) return;
      const worker = setupWorker(...createHandlers(http, HttpResponse));
      await worker.start({
        onUnhandledRequest: "bypass",
        serviceWorker: { url: "/mockServiceWorker.js" },
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <ApiProvider baseUrl={baseUrl}>
      <LocaleProvider>
        <ToastProvider>
          <Gates>{children}</Gates>
        </ToastProvider>
      </LocaleProvider>
    </ApiProvider>
  );
}
